'use client'

import React from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import VolunteersCardsMobile from './volunteers-cards-mobile'
import VolunteersList from './volunteers-list'
import Pagination from '@/shared/components/pagination/pagination'
import PaginationDataContextProvider from '@/shared/context/pagination-data-context'
import AddVolunteerButton from './add-volunteer-button'
import VolunteerFilterDialog from './volunteer-filter-button'
import VolunteerSearchInput from './volunteer-search-input'
import VolunteerDownloadButton from './volunteer-download-button'
import { useRegionOptions } from '@/shared/hooks/use-region-options'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

interface FilterSearchVolunteersTabProps {
  canManageVolunteers: boolean
}

function RegionalRosterSelect() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { options, isLoading } = useRegionOptions(true)
  const value = searchParams.get('serveDatesRegionId') ?? 'all'

  const handleChange = (nextValue: string) => {
    const params = new URLSearchParams(searchParams.toString())
    if (nextValue === 'all') {
      params.delete('serveDatesRegionId')
    } else {
      params.set('serveDatesRegionId', nextValue)
      params.set('limit', '50')
    }
    params.set('page', '1')
    router.push(`${pathname}?${params.toString()}`)
  }

  return (
    <div className="w-full sm:w-[220px]">
      <Select value={value} onValueChange={handleChange} disabled={isLoading}>
        <SelectTrigger size="sm" className="h-10 bg-white">
          <SelectValue placeholder={isLoading ? 'Loading regions...' : 'Region'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Region</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export default function FilterSearchVolunteersTab({ canManageVolunteers }: FilterSearchVolunteersTabProps) {
  return (
    <PaginationDataContextProvider>
      <div className="border rounded px-3 py-5 space-y-4 mb-10 hidden lg:block">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-center">
            <div className="flex gap-4 items-center justify-center min-w-0">
              <VolunteerFilterDialog />
              {canManageVolunteers && <RegionalRosterSelect />}
              <div className="flex-1 w-full">
                <VolunteerSearchInput />
              </div>
            </div>
            {canManageVolunteers && (
              <div className="flex items-center justify-end gap-3">
                <AddVolunteerButton />
                <VolunteerDownloadButton />
              </div>
            )}
          </div>
        </div>
        <VolunteersList canManageVolunteers={canManageVolunteers} />
        <Pagination />
      </div>
      <VolunteersCardsMobile />
    </PaginationDataContextProvider>
  )
}
